import { jsxs, jsx } from 'react/jsx-runtime';
import classNames from 'classnames';
import { createContext, useRef, useState, useEffect, useContext } from 'react';
import { Snackbar } from '../Snackbar/Snackbar.js';
import { SnackbarQueue } from '../SnackbarQueue/SnackbarQueue.js';

const SnackbarQueueContext = createContext({
    /**
     * @param content - Text or ReactNode for Snackbar message.
     * @param options.buttonText - (optional) If `buttonText` AND `onButtonClick` are provided, a button will be shown with the corresponding text and click handler. Replaces the default "close" (✖️) button if used. Only recommended or "undo" actions.
     * @param options.duration - (optional) number of seconds (between 4-15) the snackbar should be shown. Defaults to 4.
     * @param options.onButtonClick - (optional) If `buttonText` AND `onButtonClick` are provided, a button will be shown with the corresponding text and click handler. Replaces the default "close" (✖️) button if used. Only recommended or "undo" actions.
     * @param options.variant - (optional) One of `caution`, `error`, `info` (default), `success`, or `warning` (deprecated alias to `error`).
     */
    // TODO remove this fix
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    addSnackbar: (content, options) => { },
});
const ANIM_IN_CLASS = 'z-snackbar--anim-in';
const ANIM_OUT_CLASS = 'z-snackbar--anim-out';
const DURATION_MIN = 4;
const DURATION_MAX = 15;
/**
 * [design (Figma)](https://www.figma.com/design/MUnGlxUUspXSbRkFsEXWGQ/%F0%9F%92%A0-Core-Components?node-id=8604-59106&node-type=section&t=L12XIxh9aK1lGzgC-0)
 * | [source code](https://src.private.zooplus.net/projects/FE/repos/ui-components/browse/src/components/Snackbar/Snackbar/SnackbarQueueProvider.tsx)
 *
 * Displays a snackbar message in the bottom right corner of the viewport.
 * Individual messages are displayed for 4-15 seconds (this is configurable and defaults to 4 seconds).
 * Only one message is shown at a time, so when multiple messages are added to the queue, the oldest queued message is displayed after the current message animates out.
 *
 * @param {string} bottomOffset - (optional) A CSS length value to override the bottom screen offset (useful for repositioning the snackbar queue when the scroll-to-top button is visible).
 * @param {string} closeButtonAriaLabel - This text will be used to label the "close" (✖️) button of each Snackbar element (it should be a short label like "Close" translated to the current language).
 */
const SnackbarQueueProvider = (props) => {
    const { bottomOffset, closeButtonAriaLabel } = props;
    // TODO remove this fix
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const wrapper = useRef();
    const [snackbarList, setSnackbarList] = useState([]);
    useEffect(() => {
        const queueElem = wrapper.current;
        // TODO remove this fix
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        const onAnimationend = (event) => {
            const snackbarElem = event.target;
            const snackbarData = snackbarList.find((el) => el.id === snackbarElem.dataset?.zSnackbarId);
            if (!snackbarData || !snackbarElem.classList.contains(ANIM_IN_CLASS)) {
                return;
            }
            if (snackbarElem.classList.contains(ANIM_OUT_CLASS)) {
                // snackbar has just finished animating out, so remove it from the list
                setSnackbarList(snackbarList.slice(1));
                return;
            }
            // snackbar has just finished animating in, so prepare it to animate out
            animateOutSnackbar(snackbarData, true);
        };
        queueElem.addEventListener('animationend', onAnimationend);
        return () => {
            queueElem.removeEventListener('animationend', onAnimationend);
        };
    });
    const addSnackbar = (content, options) => setSnackbarList([
        ...snackbarList,
        ...[
            {
                content,
                options: options || {},
                id: `${Date.now()}${Math.random()}`,
            },
        ],
    ]);
    const animateOutSnackbar = (snackbarData, delay) => {
        const snackbarElem = document.querySelector(`.z-snackbar[data-z-snackbar-id="${snackbarData.id}"]`);
        if (!snackbarElem || !(snackbarElem instanceof HTMLElement)) {
            return;
        }
        if (!delay) {
            snackbarElem?.classList.add(ANIM_OUT_CLASS);
            return;
        }
        const customDuration = snackbarData?.options?.duration || 0;
        let duration = DURATION_MIN;
        if (customDuration >= DURATION_MAX) {
            duration = DURATION_MAX;
        }
        else if (customDuration >= DURATION_MIN) {
            duration = customDuration;
        }
        setTimeout(() => {
            snackbarElem?.classList.add(ANIM_OUT_CLASS);
        }, duration * 1000);
    };
    return (jsxs(SnackbarQueueContext.Provider, { value: { addSnackbar }, children: [props.children, jsx(SnackbarQueue, { ref: wrapper, bottomOffset: bottomOffset, children: snackbarList.map((snackbarItem, idx) => {
                    const opts = snackbarItem.options;
                    return (jsx(Snackbar, { buttonText: opts?.buttonText, className: classNames({
                            [ANIM_IN_CLASS]: idx === 0,
                        }), closeButtonAriaLabel: closeButtonAriaLabel, "data-z-snackbar-id": snackbarItem.id, onButtonClick: (event) => {
                            opts.onButtonClick && opts.onButtonClick(event);
                            animateOutSnackbar(snackbarItem);
                        }, onClose: () => {
                            animateOutSnackbar(snackbarItem);
                        }, variant: opts?.variant, children: snackbarItem.content }, snackbarItem.id));
                }) })] }));
};
const useSnackbarQueue = () => useContext(SnackbarQueueContext);

export { SnackbarQueueProvider, useSnackbarQueue };
//# sourceMappingURL=SnackbarQueueProvider.js.map
